import type {Criterion, CriterionField, ICriterionFactory} from './criteria.types';
import type {Observable, Subject} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import {removeAll, byReference} from 'shared/utils/array-utils';

export interface CriterionsFormManagerState {
    criterions: Criterion[];
    isAnyCriterionFilled: boolean;
    notEmptyCriterions: Criterion[];
}

export default class CriterionsFormManager {

    private criterions: Criterion[] = [];
    private readonly reactiveState$: Subject<CriterionsFormManagerState>;

    constructor(private criterionFactory: ICriterionFactory) {
        this.reactiveState$ = new BehaviorSubject(this.createState());
    }

    public getState$(): Observable<CriterionsFormManagerState> {
        return this.reactiveState$.pipe();
    }

    public setCriterions(criterions: Criterion[]) {
        this.criterions = criterions;
        this.emitCurrentState();
    }

    public getCriterions(): Criterion[] {
        return this.criterions;
    }

    public clearCriterions() {
        this.criterions = [];
        this.emitCurrentState();
    }

    public addCriterion(field: CriterionField) {
        this.criterions.push(this.createCriterionByField(field));
        this.emitCurrentState();
    }

    public removeCriterion(criterion: Criterion) {
        this.criterions = removeAll(this.criterions, byReference(criterion));
        this.emitCurrentState();
    }

    public updateCriterionByField(criterion: Criterion, field: CriterionField) {
        this.criterions[this.criterions.indexOf(criterion)] = this.createCriterionByField(field);
        this.emitCurrentState();
    }

    public updateCriterionValue(criterion: Criterion, newValue: any) {
        this.criterions[this.criterions.indexOf(criterion)].value = newValue;
        this.emitCurrentState();
    }

    public clearCriterionValues() {
        this.criterions.forEach((crit) => crit.clear());
        this.emitCurrentState();
    }

    public isAnyCriterionFilled(): boolean {
        return this.criterions.some((crit) => !crit.isEmpty());
    }

    public getNotEmptyCriterions(): Criterion[] {
        return this.criterions.filter((crit) => !crit.isEmpty());
    }

    public createCriterionByField(field: CriterionField): Criterion {
        switch (field.editor.type) {
            case 'search-text':
            case 'text-with-metadata':
                return this.criterionFactory.createTextCriterion(field);

            case 'number-range':
            case 'range':
                return this.criterionFactory.createBetweenCriterion(field);

            case 'single-acceptable':
            case 'multiple-acceptable':
            case 'acceptable-root':
                return this.criterionFactory.createInCriterion(field);

            case 'date':
            case 'year':
            case 'scannable-text':
                return this.criterionFactory.createEqCriterion(field);
        }

        throw new Error(`Unsupported editor ${field.editor.type}`);
    }

    private emitCurrentState() {
        this.reactiveState$.next(this.createState());
    }

    private createState(): CriterionsFormManagerState {
        return {
            criterions: this.criterions,
            isAnyCriterionFilled: this.isAnyCriterionFilled(),
            notEmptyCriterions: this.getNotEmptyCriterions()
        };
    }
}